import React, { useEffect, useMemo, useState } from "react";
import DataTable, { ColumnDef } from "@/components/utils/DataTable";
import { pageStyles } from "@/components/utils/PageLayout";
import { Button, FilterMultiSelect, FilterOption } from "@/components/ui";
import { DateRangeFilter } from "@/components/utils/filters";
import { portfolioTransactionAPI } from "@/services/api";
import { CurrencyCode, formatCurrency, formatDate } from "@/utils/formatters";
import { sumBy } from "@/utils/aggregate";

type SchemeData = {
  scheme_id: number;
  scheme_name: string;
};

type ClientData = {
  client_id: number;
  client_name: string;
};

type TransactionData = {
  date_pur_acqui: string;
  client_id: number;
  client_name: string;
  scheme_id: number;
  scheme_name: string;
  cs_plus: number;
  cs_minus: number;
  opi: number;
  opo: number;
};

type PortfolioTransactionResponse = {
  count: number;
  portfolio_transactions: TransactionData[];
  client_data: ClientData[];
  scheme_data: SchemeData[];
  summary: {
    total_cs_plus: number;
    total_cs_minus: number;
    total_opi: number;
    total_opo: number;
  };
};

const yesterday = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
};

// Column keys carrying a total in the table footer.
const TOTAL_KEYS = ["cs_plus", "cs_minus", "opi", "opo"] as const;
type TotalKey = typeof TOTAL_KEYS[number];

const NUMBER_FIELDS: (keyof TransactionData)[] = ["cs_plus", "cs_minus", "opi", "opo"];

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const fmt = (amount: number): string => formatCurrency(amount, CurrencyCode.INR, 0);

const PortfolioTransaction: React.FC = () => {
  const [data, setData] = useState<PortfolioTransactionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedClients, setSelectedClients] = useState<FilterOption[]>([]);
  const [selectedSchemes, setSelectedSchemes] = useState<FilterOption[]>([]);
  const [fromDate, setFromDate] = useState(yesterday);
  const [toDate, setToDate] = useState(yesterday);
  // The range the rows on screen actually cover — set on a completed fetch, so
  // changing a date input does not relabel data that has not been reloaded.
  const [appliedRange, setAppliedRange] = useState<{ from: string; to: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<keyof TransactionData>("date_pur_acqui");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Parameters<typeof portfolioTransactionAPI.getPortfolioTransactions>[0] = {};
      if (selectedClients.length > 0) params.client_ids = selectedClients.map(o => Number(o.value));
      if (selectedSchemes.length > 0) params.scheme_ids = selectedSchemes.map(o => Number(o.value));
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const response = await portfolioTransactionAPI.getPortfolioTransactions(params);

      const uniqueClients = [...new Map(
        (Array.isArray(response.client_data) ? response.client_data : []).map((c: ClientData) => [c.client_id, c] as const)
      ).values()] as ClientData[];
      uniqueClients.sort((a, b) => a.client_name.localeCompare(b.client_name));

      const uniqueSchemes = [...new Map(
        (Array.isArray(response.scheme_data) ? response.scheme_data : []).map((s: SchemeData) => [s.scheme_id, s] as const)
      ).values()] as SchemeData[];
      uniqueSchemes.sort((a, b) => a.scheme_name.localeCompare(b.scheme_name));

      setData({ ...response, client_data: uniqueClients, scheme_data: uniqueSchemes });
      setAppliedRange({ from: fromDate, to: toDate });
      setCurrentPage(1);
    } catch (err) {
      const apiErr = err as { response?: { data?: { error?: string } }; message?: string };
      setError(apiErr?.response?.data?.error || apiErr?.message || "Failed to fetch portfolio transaction data.");
      setData(null);
    } finally {
      setHasFetched(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (field: string) => {
    const f = field as keyof TransactionData;
    if (sortBy === f) {
      setSortDir(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(f);
      setSortDir("desc");
    }
    setCurrentPage(1);
  };

  const clientOptions: FilterOption[] = useMemo(
    () => (data?.client_data ?? []).map(c => ({
      value: c.client_id.toString(),
      label: `${c.client_name} (ID: ${c.client_id})`,
    })),
    [data],
  );

  const schemeOptions: FilterOption[] = useMemo(
    () => (data?.scheme_data ?? []).map(s => ({
      value: s.scheme_id.toString(),
      label: `${s.scheme_name} (ID: ${s.scheme_id})`,
    })),
    [data],
  );

  const filteredAndSorted = useMemo(() => {
    if (!data) return [] as TransactionData[];
    const query = searchTerm.toLowerCase();

    const result = data.portfolio_transactions.filter(item =>
      item.client_name.toLowerCase().includes(query) ||
      item.scheme_name.toLowerCase().includes(query) ||
      item.date_pur_acqui.includes(searchTerm) ||
      item.client_id.toString().includes(searchTerm) ||
      item.scheme_id.toString().includes(searchTerm)
    );

    return [...result].sort((a, b) => {
      let valA: any = a[sortBy];
      let valB: any = b[sortBy];

      if (sortBy === "date_pur_acqui") {
        valA = new Date(valA);
        valB = new Date(valB);
      } else if (!NUMBER_FIELDS.includes(sortBy)) {
        valA = String(valA ?? "").toLowerCase();
        valB = String(valB ?? "").toLowerCase();
      }

      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, searchTerm, sortBy, sortDir]);

  const totalCount = filteredAndSorted.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const paginatedData = useMemo(
    () => filteredAndSorted.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredAndSorted, currentPage, pageSize],
  );

  // Mirrors the on-screen column order. The export carries data rows only — a
  // totals row would be double-counted by anything that sums the file.
  const csvColumns: { header: string; value: (r: TransactionData) => string | number }[] = [
    { header: "Date", value: r => r.date_pur_acqui },
    { header: "Client ID", value: r => r.client_id },
    { header: "Client Name", value: r => r.client_name },
    { header: "Scheme ID", value: r => r.scheme_id },
    { header: "Scheme Name", value: r => r.scheme_name },
    { header: "Corpus In (CS+)", value: r => r.cs_plus },
    { header: "Corpus Out (CS-)", value: r => r.cs_minus },
    { header: "Stock In (OPI)", value: r => r.opi },
    { header: "Stock Out (OPO)", value: r => r.opo },
  ];

  const downloadCSV = () => {
    if (!filteredAndSorted.length) return;
    const esc = (v: string | number) =>
      typeof v === "number" ? String(v) : `"${v.replace(/"/g, '""')}"`;
    const csv = [
      csvColumns.map(c => esc(c.header)).join(","),
      ...filteredAndSorted.map(r => csvColumns.map(c => esc(c.value(r))).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `portfolio_transactions_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const baseColumns: ColumnDef<TransactionData>[] = [
    {
      key: "date_pur_acqui",
      header: "Date",
      sortable: true,
      render: row => formatDate(row.date_pur_acqui),
      renderFooter: () => "Totals",
    },
    {
      key: "client_name",
      header: "Client",
      sortable: true,
      render: row => (
        <>
          {row.client_name}
          <br />
          <span className={pageStyles['cell-sub']}>Account No: {row.client_id}</span>
        </>
      ),
      renderFooter: rows => (
        <span className={pageStyles['cell-sub']}>
          {rows.length} transaction{rows.length === 1 ? "" : "s"} (all pages)
        </span>
      ),
    },
    {
      key: "scheme_name",
      header: "Scheme",
      sortable: true,
      render: row => (
        <>
          {row.scheme_name}
          <br />
          <span className={pageStyles['cell-sub']}>ID: {row.scheme_id}</span>
        </>
      ),
    },
    { key: "cs_plus", header: "Corpus In (CS+)", sortable: true, align: "right", render: row => fmt(row.cs_plus) },
    { key: "cs_minus", header: "Corpus Out (CS-)", sortable: true, align: "right", render: row => fmt(row.cs_minus) },
    { key: "opi", header: "Stock In (OPI)", sortable: true, align: "right", render: row => fmt(row.opi) },
    { key: "opo", header: "Stock Out (OPO)", sortable: true, align: "right", render: row => fmt(row.opo) },
  ];

  // Totals are attached by column key, so a figure can never land under the wrong header.
  const columns: ColumnDef<TransactionData>[] = baseColumns.map(col =>
    TOTAL_KEYS.includes(col.key as TotalKey)
      ? { ...col, renderFooter: rows => fmt(sumBy(rows, col.key as TotalKey)) }
      : col
  );

  const activeFilterCount = (selectedClients.length > 0 ? 1 : 0) + (selectedSchemes.length > 0 ? 1 : 0);

  const clearFilters = () => {
    setSelectedClients([]);
    setSelectedSchemes([]);
  };

  const filterSlot = (
    <>
      <FilterMultiSelect
        label="Client"
        value={selectedClients}
        onChange={setSelectedClients}
        options={clientOptions}
      />

      <FilterMultiSelect
        label="Scheme"
        value={selectedSchemes}
        onChange={setSelectedSchemes}
        options={schemeOptions}
      />

      <DateRangeFilter
        startDate={fromDate}
        endDate={toDate}
        onStartDateChange={setFromDate}
        onEndDateChange={setToDate}
        startId="pt-from-date"
        endId="pt-to-date"
      />

      <div className={pageStyles['filter-action']}>
        <Button variant="primary" onClick={fetchData} disabled={loading}>
          {loading ? "Loading…" : "Search"}
        </Button>
      </div>
    </>
  );

  return (
    <DataTable
      title="Portfolio Transactions"
      subtitle={appliedRange
        ? `Corpus and stock movements from ${formatDate(appliedRange.from)} to ${formatDate(appliedRange.to)}`
        : undefined}
      data={paginatedData}
      footerRows={filteredAndSorted}
      columns={columns}
      rowKey={(row, index) => `${row.date_pur_acqui}-${row.client_id}-${row.scheme_id}-${index}`}
      totalCount={totalCount}
      loading={loading}
      loadingMessage="Loading portfolio transactions…"
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
      sortField={sortBy}
      sortDir={sortDir}
      onSort={handleSort}
      searchQuery={searchTerm}
      onSearchChange={value => { setSearchTerm(value); setCurrentPage(1); }}
      searchPlaceholder="Search by client, scheme, date or ID…"
      filterSlot={filterSlot}
      activeFilterCount={activeFilterCount}
      onClearFilters={clearFilters}
      onDownloadCSV={downloadCSV}
      emptyMessage={hasFetched
        ? "No portfolio transactions found for the selected filters."
        : "Select filters and click Search to view portfolio transactions."}
      fitContent
    />
  );
};

export default PortfolioTransaction;
